import { Button, Card, Toast } from "@ebsco-ui/ebsco-ui";
import { FORM_ERROR } from "final-form";
import createDecorator from "final-form-focus";
import PropTypes from "prop-types";
import React from "react";
import { Form } from "react-final-form";
import { FormattedMessage, injectIntl } from "react-intl";
import { connect } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { compose } from "redux";
import * as yup from "yup";
import { BREAKPOINTS } from "../../../../constants";
import sharedMessages from "../../../../shared-messages";
import {
  updatePasswordFailure,
  updatePasswordSuccess
} from "../../../store/password";
import { getEmail, getUsername } from "../../../store/personal-user";
import submitErrorFieldsHaveChanged from "../../../utils/submit-error-fields-have-changed";
import { useEdgeApiContext } from "../../edge-api";
import FormErrorNotification from "../../form-error-notification";
import { Password, PasswordInputField } from "../../shared/fields";
import messages from "./password-update-form-messages";

import "./password-update-form.scss";

const focusOnErrors = createDecorator();

const PasswordUpdateForm = props => {
  const {
    intl: { formatMessage },
    email,
    username,
    updatePasswdSuccess,
    updatePasswdFailure
  } = props;

  const personalUserApi = useEdgeApiContext();

  const isMobile = useMediaQuery({
    query: `(max-width: ${BREAKPOINTS.SCREEN.PHONE}px)`
  });

  const currentPasswordValidationSchema = yup.object({
    value: yup.string().required(formatMessage(messages.requireCurrentPassword))
  });

  const confirmPasswordValidationSchema = yup.object({
    value: yup
      .string()
      .required(formatMessage(sharedMessages.requireConfirmPassword))
      .test(
        "match-new-password",
        formatMessage(sharedMessages.passwordsDoNotMatch),
        function(value) {
          return value === this.parent.allFormValues.newPassword;
        }
      )
  });

  const onSubmit = async (data, { reset }) => {
    let formErrors;
    try {
      await personalUserApi.putPassword(data);
      Toast(formatMessage(messages.passwordUpdated), {
        closeButton: false,
        type: "success"
      });
      updatePasswdSuccess();
      setTimeout(reset);
    } catch (error) {
      updatePasswdFailure(error);
      formErrors = {
        [FORM_ERROR]: {
          headerMessage: formatMessage(sharedMessages.somethingWentWrong),
          descriptionMessage: formatMessage(messages.unableToUpdatePassword)
        }
      };

      if (error.response?.status === 403) {
        formErrors = {
          currentPassword: [formatMessage(messages.incorrectCurrentPassword)]
        };
      }
    }
    return formErrors;
  };

  return (
    <Card
      isRaised
      isRounded={!isMobile}
      isTransparent={false}
      border="all"
      className="password-update-form"
    >
      <Form onSubmit={onSubmit} decorators={[focusOnErrors]}>
        {({
          dirty,
          dirtyFieldsSinceLastSubmit,
          handleSubmit,
          submitFailed,
          submitting,
          submitError,
          submitErrors
        }) => {
          const disableSave =
            submitting ||
            !(submitFailed
              ? submitErrorFieldsHaveChanged(
                  submitErrors,
                  dirtyFieldsSinceLastSubmit
                )
              : dirty);

          return (
            <form onSubmit={handleSubmit}>
              {submitError && <FormErrorNotification {...submitError} />}
              <PasswordInputField
                id="current-password"
                name="currentPassword"
                label={formatMessage(messages.currentPassword)}
                aria-required="true"
                validationSchema={currentPasswordValidationSchema}
              />
              <Password
                email={email}
                username={username}
                label={formatMessage(sharedMessages.newPassword)}
                id="new-password"
                name="newPassword"
              />
              <PasswordInputField
                id="confirm-password"
                name="confirmPassword"
                label={formatMessage(sharedMessages.confirmPassword)}
                aria-required="true"
                validationSchema={confirmPasswordValidationSchema}
              />
              <Button
                className="change-password-button"
                type="submit"
                disabled={disableSave}
                styleType="solid"
              >
                <FormattedMessage {...messages.changePassword} />
              </Button>
            </form>
          );
        }}
      </Form>
    </Card>
  );
};

PasswordUpdateForm.propTypes = {
  email: PropTypes.string.isRequired,
  username: PropTypes.string.isRequired
};

const mapStateToProps = state => ({
  email: getEmail(state),
  username: getUsername(state)
});

const mapDispatchToProps = {
  updatePasswdSuccess: updatePasswordSuccess,
  updatePasswdFailure: updatePasswordFailure
};

export { PasswordUpdateForm };
export default compose(
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps)
)(PasswordUpdateForm);
